/**
 * K3Page - K3 Årsredovisning cluster page
 *
 * SEO Target: /arsredovisning/k3
 * Primary keywords: K3 årsredovisning, kassaflödesanalys, uppskjuten skatt, BFNAR 2012:1
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { AuthModal } from "@/components/auth/AuthModal";

const CheckIcon = () => (
  <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const ArrowRightIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

export function K3Page() {
  const [showAuthModal, setShowAuthModal] = useState(false);

  const breadcrumbItems = [
    { label: "Årsredovisning", href: "/arsredovisning" },
    { label: "K3" },
  ];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Årsredovisning", url: "/arsredovisning" },
    { name: "K3", url: "/arsredovisning/k3" },
  ]);

  return (
    <>
      <PageMeta
        title="K3 Årsredovisning Online | Med Kassaflödesanalys | Kvanten"
        description="Skapa K3-årsredovisning enligt BFNAR 2012:1 med kassaflödesanalys, uppskjuten skatt och komponentavskrivning. För större företag eller frivilligt val."
        keywords="K3 årsredovisning, BFNAR 2012:1, kassaflödesanalys, uppskjuten skatt, komponentavskrivning, större företag årsredovisning"
        canonicalUrl={`${SEO_CONFIG.siteUrl}/arsredovisning/k3`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="max-w-3xl">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-sm font-medium mb-4">
              Huvudregelverket
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              K3 Årsredovisning med Kassaflödesanalys
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              K3 (BFNAR 2012:1) är huvudregelverket för årsredovisning i Sverige. Obligatorisk
              kassaflödesanalys, uppskjuten skatt och detaljerad redovisning. Kvanten stödjer
              hela K3-processen.
            </p>

            <div className="flex flex-wrap gap-4 mb-8">
              <Link
                to="/wizard"
                className="inline-flex items-center gap-2 px-6 py-3 text-lg font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Skapa K3-årsredovisning
                <ArrowRightIcon />
              </Link>
              <button
                onClick={() => setShowAuthModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 text-lg font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Skapa konto
              </button>
            </div>

            <div className="flex flex-wrap gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckIcon />
                <span>Gratis att testa</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckIcon />
                <span>Kassaflödesanalys ingår</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckIcon />
                <span>Uppskjuten skatt beräknas</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who must use K3 */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Vem måste använda K3?</h2>
              <p className="text-gray-600 mb-6">
                K3 är obligatoriskt för <strong>större företag</strong> och för företag som inte
                får tillämpa K2. Ett företag är "större" om det överskrider minst två av
                följande gränsvärden under vart och ett av de två senaste räkenskapsåren:
              </p>
              <ul className="space-y-4 mb-6">
                {[
                  "Medelantal anställda: mer än 50",
                  "Balansomslutning: mer än 40 miljoner kr",
                  "Nettoomsättning: mer än 80 miljoner kr",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Dessa MÅSTE använda K3
              </h3>
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="space-y-3">
                  {[
                    "Större företag (över gränsvärdena)",
                    "Moderföretag som upprättar koncernredovisning",
                    "Publika aktiebolag",
                    "Bostadsrättsföreningar (från 2026)",
                    "Företag med kryptotillgångar (från 2026)",
                    "Företag som frivilligt väljer K3",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckIcon />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-sm text-gray-500">
                Även mindre företag kan <strong>frivilligt</strong> välja K3 om de vill ha en
                mer detaljerad redovisning eller förbereder sig för tillväxt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* K3 Requirements */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Vad skiljer K3 från K2?</h2>
            <p className="text-xl text-gray-600">
              K3 har strängare krav men ger också en mer komplett bild av företaget
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Kassaflödesanalys",
                description:
                  "Obligatorisk för K3. Visar hur pengar flödar in och ut ur företaget uppdelat på löpande verksamhet, investeringar och finansiering.",
                badge: "Obligatorisk",
              },
              {
                title: "Uppskjuten skatt",
                description:
                  "K3 kräver redovisning av uppskjuten skatt på temporära skillnader mellan bokförda och skattemässiga värden.",
                badge: "Obligatorisk",
              },
              {
                title: "Komponentavskrivning",
                description:
                  "Anläggningstillgångar med väsentliga komponenter ska delas upp och skrivas av separat.",
                badge: "Vid behov",
              },
              {
                title: "Fler noter",
                description:
                  "K3 kräver 25-30 noter jämfört med K2:s cirka 15. Mer detaljerad information om redovisningsprinciper och poster.",
                badge: "Mer omfattande",
              },
              {
                title: "Jämförelsetal i noter",
                description:
                  "K3 kräver jämförelsetal för föregående år i alla noter, inte bara i resultat- och balansräkning.",
                badge: "Obligatorisk",
              },
              {
                title: "Finansiella instrument",
                description:
                  "Möjlighet att värdera finansiella instrument till verkligt värde istället för anskaffningsvärde.",
                badge: "Valfritt",
              },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                  <span className="text-xs font-medium px-2 py-1 bg-blue-100 text-blue-700 rounded">
                    {item.badge}
                  </span>
                </div>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cash flow explanation */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Kassaflödesanalys - Så fungerar det
              </h2>
              <p className="text-gray-600 mb-6">
                Kassaflödesanalysen visar varifrån pengarna kommer och vart de går. Den är
                uppdelad i tre delar och kan upprättas med indirekt eller direkt metod.
              </p>
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900">Löpande verksamhet</h4>
                  <p className="text-sm text-gray-600">
                    Kassaflöde från den dagliga verksamheten - försäljning, inköp, löner etc.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900">Investeringsverksamhet</h4>
                  <p className="text-sm text-gray-600">
                    Köp och försäljning av anläggningstillgångar, investeringar i dotterbolag.
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900">Finansieringsverksamhet</h4>
                  <p className="text-sm text-gray-600">
                    Nyemissioner, utdelningar, upptagna och amorterade lån.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Kvanten beräknar automatiskt
              </h3>
              <p className="text-gray-600 mb-6">
                Vår intelligenta motor analyserar din SIE-fil och genererar kassaflödesanalysen
                automatiskt. Du behöver bara komplettera med uppgifter som inte finns i bokföringen.
              </p>
              <ul className="space-y-3">
                {[
                  "Automatisk klassificering av transaktioner",
                  "Beräkning enligt indirekt metod",
                  "Justering för icke-kassaflödespåverkande poster",
                  "Validering mot resultat- och balansräkning",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* K3 specific notes */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Vad ingår i en K3-årsredovisning?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Obligatoriska delar</h3>
              <ul className="space-y-3">
                {[
                  "Förvaltningsberättelse med verksamhetsbeskrivning",
                  "Flerårsöversikt (4 år)",
                  "Förändring av eget kapital",
                  "Resultatdisposition",
                  "Resultaträkning",
                  "Balansräkning",
                  "Kassaflödesanalys",
                  "Noter och tilläggsupplysningar (25-30 st)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-xl p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">K3-specifika noter</h3>
              <ul className="space-y-3">
                {[
                  "Kassaflödesanalys (med tilläggsupplysningar)",
                  "Uppskjuten skatt (med beräkning)",
                  "Komponentavskrivning för anläggningar",
                  "Finansiella instrument (om tillämpligt)",
                  "Jämförelsetal i alla noter",
                  "Koncernupplysningar (om tillämpligt)",
                  "Eventualförpliktelser (utökad)",
                  "Ställda säkerheter (utökad)",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckIcon />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Legal references */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Juridisk grund</h2>
          <p className="text-gray-600 mb-8">
            K3-årsredovisning följer dessa regelverk:
          </p>
          <div className="bg-gray-50 rounded-xl p-8">
            <ul className="space-y-4">
              <li>
                <strong className="text-gray-900">BFNAR 2012:1</strong>
                <span className="text-gray-600">
                  {" "}
                  - Bokföringsnämndens allmänna råd om årsredovisning och koncernredovisning (K3)
                </span>
              </li>
              <li>
                <strong className="text-gray-900">ÅRL (1995:1554)</strong>
                <span className="text-gray-600"> - Årsredovisningslagen</span>
              </li>
              <li>
                <strong className="text-gray-900">BFNAR 2006:11</strong>
                <span className="text-gray-600"> - Gränsvärden för större/mindre företag</span>
              </li>
              <li>
                <strong className="text-gray-900">IAS/IFRS</strong>
                <span className="text-gray-600">
                  {" "}
                  - K3 är baserat på IFRS for SMEs (internationella standarder)
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Behöver du K3-årsredovisning?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Kvanten stödjer hela K3-processen inklusive kassaflödesanalys och uppskjuten skatt.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/wizard"
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-blue-600 bg-white rounded-lg hover:bg-blue-50 transition-colors"
            >
              Prova direkt
              <ArrowRightIcon />
            </Link>
            <button
              onClick={() => setShowAuthModal(true)}
              className="inline-flex items-center gap-2 px-8 py-4 text-lg font-medium text-white border-2 border-white/30 rounded-lg hover:bg-white/10 transition-colors"
            >
              Skapa konto
            </button>
          </div>
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="register"
      />
    </>
  );
}
